/**
 * pages/hazard/Edit.js — Аюулын мэдээллийг ЗАСАХ (web)
 *
 * Route: /hazard/:id/edit
 *
 * ⚠ ДҮРЭМ (хэрэглэгч 2026-11-01): «оруулсан ажилтан л хэсэгхэн хугацаанд
 *   засах боломжтой». Backend нь үүнийг АЛЬ ХЭДИЙН хэрэгжүүлсэн —
 *   `Resources/mobile/hazard_scope.py :: _is_editable`:
 *       • зөвхөн ЗОХИОГЧ (`insert_user_tn`)
 *       • зөвхөн NEW төлөвт
 *       • `insert_date + EDIT_WINDOW_MINUTES` (одоогоор 2 минут)
 *   Иймд энд дүрмийг ДАХИН БИЧИХГҮЙ: сервер `is_editable` ·
 *   `editable_until` тугийг цувруулдаг тул ТЭДГЭЭРЭЭР барина. Хуулбарлавал
 *   хоёр тал зөрч, товч дарагдаад 403 буцах «урхи» болно.
 *
 * ⚠ ЯАГААД ЭНЭ ХУУДАС ХЭРЭГТЭЙ БОЛОВ: `/hazard/:id/edit` гэсэн route
 *   ОГТ байхгүй байсан тул хэрэглэгч тэр хаягаар ороход ХООСОН ЦАГААН
 *   дэлгэц гардаг байв (`router.js`-д catch-all ч байхгүй). Register нь
 *   зөвхөн ШИНЭЭР үүсгэдэг, Detail нь зөвхөн харуулдаг.
 *
 * ⚠ Зөвхөн backend-ийн `allowed` олонлогт байгаа талбарыг явуулна
 *   (`Mobile_HazardReportID.put`). Бусдыг явуулбал чимээгүй ХАЯГДАНА.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import { getCategories, getHazardById, updateHazard } from "src/api/hazard";
import { useUserContext } from "src/contexts/userContext";

const SEVERITIES = [
  { value: 1, label: "Бага" },
  { value: 2, label: "Дунд" },
  { value: 3, label: "Өндөр" },
];

/** `editable_until`-ээс хойшхи үлдсэн СЕКУНД (сөрөг бол 0). */
function secondsLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return ms > 0 ? Math.floor(ms / 1000) : 0;
}

function mmss(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** `datetime-local` input-д тохирох хэлбэр (секунд, цагийн бүсгүй). */
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

const Edit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUserContext();

  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [left, setLeft] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getHazardById(id)
      .then((d) => {
        setData(d || null);
        if (d) {
          setForm({
            description: d.description || "",
            severity: Number(d.severity) || 2,
            categoryIds: (() => {
              if (Array.isArray(d.categories) && d.categories.length) {
                return d.categories.map((c) => String(c.id));
              }
              if (d.hazard_category_ids) {
                return String(d.hazard_category_ids)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
              }
              return d.hazard_category_id
                ? [String(d.hazard_category_id)]
                : [];
            })(),
            locationText: d.location_text || "",
            locationDescription: d.location_description || "",
            peopleCount:
              d.affected_people_count === null ||
              d.affected_people_count === undefined
                ? ""
                : String(d.affected_people_count),
            selfInDanger: !!d.self_in_danger,
            reportDate: toLocalInput(d.report_date),
          });
          setLeft(secondsLeft(d.editable_until));
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getCategories()
      .then((d) => setCats(Array.isArray(d) ? d : d?.items || []))
      .catch(() => setCats([]));
  }, []);

  /* Үлдсэн хугацааг СЕКУНД тутам тоолно — цонх хаагдахад товч өөрөө
     түгжигдэнэ. Эс бөгөөс хэрэглэгч бөглөж байтал хугацаа дуусаад
     хадгалах дээр дарж 403 авна. */
  useEffect(() => {
    if (!data?.editable_until) return undefined;
    const t = setInterval(() => setLeft(secondsLeft(data.editable_until)), 1000);
    return () => clearInterval(t);
  }, [data]);

  /* ── ЯАГААД засаж болохгүйг ТОДОРХОЙ хэлнэ ─────────────────────────
     Сервер `is_editable` гэсэн НЭГ туг л буцаадаг тул шалтгааныг энд
     задлана. Дуугүй «болохгүй» гэвэл хэрэглэгч «систем эвдэрсэн» гэж
     ойлгоно. */
  const block = useMemo(() => {
    if (!data) return null;
    const mine = String(data.insert_user_tn || "") === String(user?.tn || "");
    if (!mine) {
      return `Зөвхөн бүртгэсэн ажилтан засна (бүртгэсэн: ${
        data.insert_user_name || data.insert_user_tn || "-"
      }).`;
    }
    if (data.status && data.status !== "NEW") {
      return "Хяналтад орсон/шийдвэрлэгдсэн мэдээллийг засахгүй — түүхэн бүрэн бүтэн байдал хамгаалагдана.";
    }
    if (left === 0) {
      return "Засах хугацаа дууссан. Залруулга хийх шаардлагатай бол ХАБ-ын мэргэжилтэнд хандана уу.";
    }
    if (!data.is_editable) return "Энэ мэдээллийг засах эрх алга.";
    return null;
  }, [data, user, left]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCat = (cid) =>
    setForm((f) => {
      const has = f.categoryIds.includes(cid);
      if (has) return { ...f, categoryIds: f.categoryIds.filter((x) => x !== cid) };
      /* 1–3 хүчин зүйл — Register-тэй ИЖИЛ хязгаар. */
      if (f.categoryIds.length >= 3) return f;
      return { ...f, categoryIds: [...f.categoryIds, cid] };
    });

  const save = async () => {
    if (!form.description.trim()) {
      Swal.fire({ title: "Аюулын тайлбарыг бөглөнө үү", icon: "warning" });
      return;
    }
    setSaving(true);
    try {
      await updateHazard(id, {
        description: form.description.trim(),
        severity: form.severity,
        hazard_category_ids: form.categoryIds.join(",") || undefined,
        hazard_category_id: form.categoryIds[0] || undefined,
        location_text: form.locationText || undefined,
        location_description: form.locationDescription || undefined,
        affected_location: form.locationText || undefined,
        affected_people_count: form.peopleCount || undefined,
        self_in_danger: form.selfInDanger,
        report_date: form.reportDate || undefined,
      });
      await Swal.fire({ title: "Хадгалагдлаа", icon: "success", timer: 1200 });
      navigate(`/hazard/${id}`);
    } catch (e) {
      /* Серверийн шалтгааныг ДАРЖ БИЧИХГҮЙ — хугацаа дууссан эсэхийг
         тэр л нарийн мэднэ (цагийн зөрүү). */
      Swal.fire({
        title: "Хадгалж чадсангүй",
        text:
          e?.response?.data?.message ||
          "Сервер татгалзлаа. Хугацаа дууссан эсвэл эрх алга байж магадгүй.",
        icon: "error",
      });
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-bg-page p-10 text-center text-text-tertiary">
        <i className="fas fa-spinner fa-spin mr-2" />
        Уншиж байна...
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-bg-page p-10 text-center">
        <p className="text-risk-high-text mb-4">
          Аюулын мэдээлэл олдсонгүй (№ {id}).
        </p>
        <button
          type="button"
          className="px-4 py-2 rounded bg-primary text-white"
          onClick={() => navigate("/hazard/dashboard")}
        >
          Жагсаалт руу
        </button>
      </div>
    );
  }

  const lbl = "block text-metadata text-text-tertiary mb-1";
  const inp =
    "w-full border border-border-default rounded px-3 py-2 text-body bg-white";

  return (
    <div className="bg-bg-page p-6 text-text-primary">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-page-title text-primary font-bold">
            Аюулын мэдээлэл засах
          </h1>
          <p className="text-metadata text-text-tertiary">
            {data.report_number || `№ ${id}`}
          </p>
        </div>
        <button
          type="button"
          className="px-4 py-2 rounded border border-border-default"
          onClick={() => navigate(`/hazard/${id}`)}
        >
          <i className="fas fa-arrow-left mr-1" />
          Дэлгэрэнгүй рүү
        </button>
      </div>

      {block ? (
        <div className="bg-white rounded-lg p-6 max-w-2xl">
          <p className="text-risk-high-text mb-2">
            <i className="fas fa-lock mr-2" />
            {block}
          </p>
          <p className="text-metadata text-text-tertiary">
            Бүртгэсэн ажилтан мэдээллээ оруулсны дараа зөвхөн хэсэг
            хугацааны дотор залруулж чадна — ингэснээр хяналтад орсон
            мэдээлэл хожим өөрчлөгдөхгүй.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg p-6 max-w-2xl">
          {/* Үлдсэн хугацаа — цонх хаагдахаас сэрэмжлүүлнэ */}
          {left !== null ? (
            <div
              className={`mb-4 text-metadata ${
                left <= 30 ? "text-risk-high-text" : "text-text-tertiary"
              }`}
            >
              <i className="fas fa-clock mr-1" />
              Засах хугацаа: <b>{mmss(left)}</b> үлдлээ
            </div>
          ) : null}

          <div className="mb-4">
            <label className={lbl}>Аюулын тайлбар</label>
            <textarea
              className={inp}
              rows={4}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className={lbl}>Аюулын хүчин зүйл (1–3)</label>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => {
                const cid = String(c.id);
                const on = form.categoryIds.includes(cid);
                return (
                  <button
                    key={cid}
                    type="button"
                    onClick={() => toggleCat(cid)}
                    className={`px-3 py-1 rounded-full text-metadata border ${
                      on
                        ? "bg-primary text-white border-primary"
                        : "border-border-default text-text-secondary"
                    }`}
                  >
                    {c.name}
                  </button>
                );
              })}
              {!cats.length ? (
                <span className="text-metadata text-text-tertiary">
                  Лавлах татагдсангүй
                </span>
              ) : null}
            </div>
          </div>

          <div className="mb-4">
            <label className={lbl}>Хүндрэлийн зэрэг</label>
            <select
              className={inp}
              value={form.severity}
              onChange={(e) => set("severity", Number(e.target.value))}
            >
              {SEVERITIES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className={lbl}>Байршил</label>
            <input
              className={inp}
              value={form.locationText}
              onChange={(e) => set("locationText", e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className={lbl}>Байршлын тодруулга</label>
            <input
              className={inp}
              value={form.locationDescription}
              onChange={(e) => set("locationDescription", e.target.value)}
            />
          </div>

          <div className="mb-4 flex gap-4">
            <div className="flex-1">
              <label className={lbl}>Хамрагдсан хүний тоо</label>
              <input
                className={inp}
                type="number"
                min={0}
                value={form.peopleCount}
                onChange={(e) => set("peopleCount", e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className={lbl}>Бүртгэсэн огноо</label>
              <input
                className={inp}
                type="datetime-local"
                value={form.reportDate}
                onChange={(e) => set("reportDate", e.target.value)}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 mb-6 text-body">
            <input
              type="checkbox"
              checked={form.selfInDanger}
              onChange={(e) => set("selfInDanger", e.target.checked)}
            />
            Би өөрөө аюулд байсан
          </label>

          <div className="flex gap-3">
            <button
              type="button"
              className="px-5 py-2 rounded bg-primary text-white disabled:opacity-50"
              disabled={saving || left === 0}
              onClick={save}
            >
              {saving ? (
                <i className="fas fa-spinner fa-spin mr-1" />
              ) : (
                <i className="fas fa-save mr-1" />
              )}
              Хадгалах
            </button>
            <button
              type="button"
              className="px-5 py-2 rounded border border-border-default"
              onClick={() => navigate(`/hazard/${id}`)}
            >
              Болих
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Edit;
